import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { QuoteEditorApi } from '../../hooks/use-quote-editor.ts';
import { fileApi } from '../../api/endpoints.ts';
import { Button, Alert, Field, Select } from '../ui.tsx';
import { WorkSection } from '../work-page.tsx';
import type { DustAssessment } from '@shared/dust-evidence.ts';
export function DustEvidenceCard({ editor, readOnly }: { editor: QuoteEditorApi; readOnly: boolean }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null);
  const q = editor.quote;
  const files = useQuery({
    queryKey: ['quote-files', q?.id],
    enabled: !!q,
    queryFn: () => fileApi.listForQuote(q!.id),
  });
  if (!q?.dust) return null;
  const state = q.dust,
    assessment = state.assessment;
  function patch(values: Partial<DustAssessment>) {
    editor.update((draft) =>
      draft.dust
        ? {
            ...draft,
            dust: {
              ...draft.dust,
              assessment: {
                lastDusted: assessment?.lastDusted ?? 'unknown',
                photoId: assessment?.photoId ?? null,
                ...values,
              },
              evidenceVerified:
                values.photoId !== undefined && values.photoId !== assessment?.photoId
                  ? false
                  : draft.dust.evidenceVerified,
            },
          }
        : draft,
    );
  }
  async function save() {
    if (busy || readOnly) return;
    setBusy(true);
    setError(null);
    try {
      if (!(await editor.save())) throw Error('unsaved');
    } catch {
      setError(t('dust.saveError'));
    } finally {
      setBusy(false);
    }
  }
  const photos = (files.data?.items ?? []).filter(
    (file) =>
      file.category === 'quote_photo' && file.contentType.startsWith('image/') && file.dustEvidenceEligible === true,
  );
  const photoOptions = [
    { value: '', label: t('dust.noPhoto') },
    ...photos.map((photo) => ({ value: photo.id, label: photo.originalFilename ?? t('dust.photo') })),
    ...(assessment?.photoId && !photos.some((photo) => photo.id === assessment.photoId)
      ? [{ value: assessment.photoId, label: t('dust.unavailable') }]
      : []),
  ];
  return (
    <WorkSection title={t('dust.heading')} description={t('dust.hint')}>
      <Field label={t('dust.age')} hint={assessment?.lastDusted ? t(`dust.${assessment.lastDusted}`) : undefined}>
        {({ id, describedBy }) => (
          <select
            id={id}
            aria-describedby={describedBy}
            className="field-control"
            disabled={readOnly || busy}
            value={assessment?.lastDusted ?? ''}
            onChange={(e) => patch({ lastDusted: e.target.value as DustAssessment['lastDusted'] })}
          >
            <option value="" disabled>
              {t('dust.choose')}
            </option>
            {['within_three_months', 'over_three_months', 'unknown'].map((value) => (
              <option key={value} value={value}>
                {t(`dust.${value}`)}
              </option>
            ))}
          </select>
        )}
      </Field>
      {assessment?.lastDusted === 'over_three_months' && (
        <>
          <Select
            label={t('dust.photo')}
            hint={
              <>
                <span className="block break-words">
                  {photoOptions.find((option) => option.value === (assessment.photoId ?? ''))?.label}
                </span>
                {t('dust.uploadHint')}
              </>
            }
            disabled={readOnly || busy || files.isLoading}
            value={assessment.photoId ?? ''}
            onChange={(e) => patch({ photoId: e.target.value || null })}
            options={photoOptions}
          />
        </>
      )}
      {state.allocationBlocked && <Alert tone="warning">{t('dust.allocationBlocked')}</Alert>}
      {q.dustStatus?.blocked && <Alert tone="warning">{t('dust.blocked')}</Alert>}
      {state.adjustmentCents > 0 && <p>{t('dust.adjustment', { percent: state.upliftBasisPoints / 100 })}</p>}
      {!readOnly && (
        <Button disabled={busy} onClick={save}>
          {t('dust.save')}
        </Button>
      )}
      {error && <Alert tone="error">{error}</Alert>}
    </WorkSection>
  );
}
